import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Car, Package, ShoppingBag, Pill, Plus, Clock, CheckCircle, 
  MapPin, ArrowRight, LogOut, User, Loader2, CreditCard, X,
  Navigation, RefreshCw, AlertTriangle, Info, Star, Route, Tag, Users, Map, ChevronDown, ChevronUp, CalendarDays, Filter, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import { jobAPI, paymentAPI, pricingAPI, ratingAPI, routeMatchAPI, tipAPI } from '../lib/api';
import { toast } from 'sonner';
import RatingDialog from '../components/RatingDialog';
import TipDialog from '../components/TipDialog';
import DisputeDialog from '../components/DisputeDialog';
import BrowseRoutes from '../components/BrowseRoutes';
import NotificationBell from '../components/NotificationBell';
import EnablePushButton from '../components/EnablePushButton';
import ChatButton from '../components/ChatButton';
import JobAutofillInput from '../components/JobAutofillInput';
import AIConciergeLauncher from '../components/AIConciergeLauncher';
import { aiDeliveryInstructions } from '../lib/ai';
import { Sparkles as SparklesIcon } from 'lucide-react';
import InteractiveMap from '../components/InteractiveMap';
import DriverTrackingMap from '../components/DriverTrackingMap';
import StoriesBar from '../components/StoriesBar';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { devError } from '../lib/devLog';

const POLL_INTERVAL = 5000; // 5 seconds

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  useOnlineStatus();
  const [jobs, setJobs] = useState([]);
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewJob, setShowNewJob] = useState(false);
  const [showBrowseRoutes, setShowBrowseRoutes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [driverLocations, setDriverLocations] = useState({});
  const [routeValidation, setRouteValidation] = useState(null);
  const [validatingRoute, setValidatingRoute] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [jobToRate, setJobToRate] = useState(null);
  const [ratedJobs, setRatedJobs] = useState(new Set());
  const [newJob, setNewJob] = useState({
    job_type: '',
    delivery_category: '',
    pickup_address: '',
    dropoff_address: '',
    notes: '',
    scheduled_at: '',
    is_scheduled: false,
    // Ride-specific
    passenger_count: '',
    // Delivery-specific
    recipient_name: '',
    recipient_phone: '',
    order_details: '',
    delivery_instructions: '',
    // Pharmacy-specific
    recipient_dob: '',
    prescription_number: '',
  });
  const [matchedRoutes, setMatchedRoutes] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [expandedTracking, setExpandedTracking] = useState({});
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showTipDialog, setShowTipDialog] = useState(false);
  const [jobToTip, setJobToTip] = useState(null);
  const [tippedJobs, setTippedJobs] = useState({});
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [jobToDispute, setJobToDispute] = useState(null);
  
  const lastPollRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const validationTimeoutRef = useRef(null);

  // Initial data fetch
  useEffect(() => {
    fetchData();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Set up polling after initial load
  useEffect(() => {
    if (!loading) {
      pollIntervalRef.current = setInterval(pollForUpdates, POLL_INTERVAL);
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [loading]);

  // Fetch driver locations for active jobs
  useEffect(() => {
    const activeJobs = jobs.filter(j => ['accepted', 'in_progress'].includes(j.status));
    if (activeJobs.length > 0) {
      fetchDriverLocations(activeJobs);
      const locationInterval = setInterval(() => fetchDriverLocations(activeJobs), 10000);
      return () => clearInterval(locationInterval);
    }
  }, [jobs]);

  // Validate route when addresses change
  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    if (newJob.pickup_address && newJob.dropoff_address && newJob.job_type) {
      validationTimeoutRef.current = setTimeout(() => {
        validateRoute();
        findMatchingRoutes();
      }, 500);
    } else {
      setRouteValidation(null);
      setMatchedRoutes([]);
    }

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [newJob.pickup_address, newJob.dropoff_address, newJob.job_type, newJob.delivery_category]);

  const findMatchingRoutes = async () => {
    if (!newJob.pickup_address || !newJob.dropoff_address) return;
    
    setLoadingMatches(true);
    try {
      const response = await routeMatchAPI.findMatches({
        pickup_address: newJob.pickup_address,
        dropoff_address: newJob.dropoff_address,
      });
      setMatchedRoutes(response.data || []);
    } catch (error) {
      devError('Route matching error:', error);
      setMatchedRoutes([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const fetchData = async () => {
    try {
      const [jobsRes, pricingRes] = await Promise.all([
        jobAPI.getAll(),
        pricingAPI.getAll(),
      ]);
      setJobs(jobsRes.data);
      setPricing(pricingRes.data);
      lastPollRef.current = new Date().toISOString();
      
      // Check tip status for completed jobs
      const completed = jobsRes.data.filter(j => j.status === 'completed' && j.driver_id);
      const tipChecks = await Promise.all(
        completed.slice(0, 20).map(j => tipAPI.getJobTip(j.id).catch(() => ({ data: { tipped: false } })))
      );
      const tipMap = {};
      completed.slice(0, 20).forEach((j, i) => {
        if (tipChecks[i]?.data?.tipped) tipMap[j.id] = tipChecks[i].data.amount;
      });
      setTippedJobs(tipMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const pollForUpdates = useCallback(async () => {
    try {
      const response = await jobAPI.poll(lastPollRef.current);
      if (response.data.count > 0) {
        setJobs(prevJobs => {
          const updatedJobIds = new Set(response.data.jobs.map(j => j.id));
          const unchangedJobs = prevJobs.filter(j => !updatedJobIds.has(j.id));
          return [...response.data.jobs, ...unchangedJobs].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
        });
      }
      lastPollRef.current = response.data.last_updated;
    } catch (error) {
      devError('Polling error:', error);
    }
  }, []);

  const fetchDriverLocations = async (activeJobs) => {
    try {
      const locationPromises = activeJobs.map(job => 
        jobAPI.getDriverLocation(job.id).catch(() => null)
      );
      const locations = await Promise.all(locationPromises);
      
      const newLocations = {};
      activeJobs.forEach((job, index) => {
        if (locations[index]?.data) {
          newLocations[job.id] = locations[index].data;
        }
      });
      setDriverLocations(newLocations);
    } catch (error) {
      devError('Failed to fetch driver locations:', error);
    }
  };

  const validateRoute = async () => {
    if (!newJob.pickup_address || !newJob.dropoff_address || !newJob.job_type) {
      return;
    }

    if (newJob.job_type === 'delivery' && !newJob.delivery_category) {
      return;
    }

    setValidatingRoute(true);
    try {
      const response = await jobAPI.validateRoute({
        pickup_address: newJob.pickup_address,
        dropoff_address: newJob.dropoff_address,
        job_type: newJob.job_type,
        delivery_category: newJob.delivery_category || null,
      });
      setRouteValidation(response.data);
    } catch (error) {
      devError('Route validation error:', error);
      setRouteValidation(null);
    } finally {
      setValidatingRoute(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    if (routeValidation && !routeValidation.is_valid) {
      toast.error(routeValidation.error_message);
      return;
    }
    
    // Category-specific validation
    if (newJob.job_type === 'delivery') {
      if (!newJob.recipient_name.trim() || !newJob.recipient_phone.trim() || !newJob.order_details.trim()) {
        toast.error('Recipient name, phone, and order details are required for deliveries');
        return;
      }
      if (newJob.delivery_category === 'pharmacy' && !newJob.recipient_dob) {
        toast.error('Recipient date of birth is required for pharmacy deliveries');
        return;
      }
    }
    
    setSubmitting(true);

    try {
      const jobData = {
        job_type: newJob.job_type,
        pickup_address: newJob.pickup_address,
        dropoff_address: newJob.dropoff_address,
        notes: newJob.notes || null,
        scheduled_at: newJob.is_scheduled && newJob.scheduled_at ? newJob.scheduled_at : null,
      };

      if (newJob.job_type === 'delivery') {
        jobData.delivery_category = newJob.delivery_category;
        jobData.recipient_name = newJob.recipient_name.trim();
        jobData.recipient_phone = newJob.recipient_phone.trim();
        jobData.order_details = newJob.order_details.trim();
        jobData.delivery_instructions = newJob.delivery_instructions.trim() || null;
        if (newJob.delivery_category === 'pharmacy') {
          jobData.recipient_dob = newJob.recipient_dob;
          jobData.prescription_number = newJob.prescription_number.trim() || null;
        }
      } else if (newJob.job_type === 'ride') {
        if (newJob.passenger_count) {
          jobData.passenger_count = parseInt(newJob.passenger_count, 10);
        }
      }

      await jobAPI.create(jobData);
      const successMsg = newJob.is_scheduled 
        ? `Job scheduled for ${new Date(newJob.scheduled_at).toLocaleString()}! Check your email.`
        : 'Job created successfully! Check your email for confirmation.';
      toast.success(successMsg);
      setShowNewJob(false);
      setNewJob({
        job_type: '',
        delivery_category: '',
        pickup_address: '',
        dropoff_address: '',
        notes: '',
        scheduled_at: '',
        is_scheduled: false,
        passenger_count: '',
        recipient_name: '',
        recipient_phone: '',
        order_details: '',
        delivery_instructions: '',
        recipient_dob: '',
        prescription_number: '',
      });
      setRouteValidation(null);
      setMatchedRoutes([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async (jobId) => {
    try {
      const originUrl = window.location.origin;
      const response = await paymentAPI.createCheckout(jobId, originUrl);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await jobAPI.cancel(jobId);
      toast.success('Job cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel job');
    }
  };

  const handleOpenRating = (job) => {
    setJobToRate(job);
    setShowRatingDialog(true);
  };

  const handleRatingSubmitted = (jobId) => {
    setRatedJobs(prev => new Set([...prev, jobId]));
  };

  // Check which jobs have been rated
  const checkRatedJobs = async (completedJobs) => {
    try {
      const rated = new Set();
      for (const job of completedJobs) {
        if (job.driver_id) {
          const response = await ratingAPI.getJobRatings(job.id).catch(() => ({ data: { ratings: [] } }));
          const userRating = response.data?.ratings?.find(r => r.rater_id === user?.id);
          if (userRating) {
            rated.add(job.id);
          }
        }
      }
      setRatedJobs(rated);
    } catch (error) {
      devError('Error checking rated jobs:', error);
    }
  };

  // Check rated jobs after jobs are loaded
  useEffect(() => {
    const completedWithDriver = jobs.filter(j => j.status === 'completed' && j.driver_id);
    if (completedWithDriver.length > 0 && user) {
      checkRatedJobs(completedWithDriver);
    }
  }, [jobs, user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      accepted: 'bg-indigo-100 text-indigo-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      initiated: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getJobIcon = (jobType, category) => {
    if (jobType === 'ride') return Car;
    const icons = {
      food: ShoppingBag,
      grocery: ShoppingBag,
      pharmacy: Pill,
      package: Package,
    };
    return icons[category] || Package;
  };

  const activeJobs = jobs.filter(j => !['completed', 'cancelled'].includes(j.status));
  const pastJobs = jobs.filter(j => ['completed', 'cancelled'].includes(j.status));

  const hubName = pricing.hub?.name || 'Mascoutah';
  const hubState = pricing.hub?.state || 'IL';

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg sm:text-xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>LocalMove</span>
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full ml-1 hidden sm:inline">{hubName}, {hubState}</span>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <button 
              onClick={fetchData}
              className="text-zinc-500 hover:text-zinc-700 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <ChatButton />
            <NotificationBell />
            <EnablePushButton compact />
            <span className="font-medium text-zinc-600 hidden md:block">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Hub Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-900 font-medium">Hub-Based Service</p>
            <p className="text-blue-700 text-sm">All rides and deliveries must start or end in {hubName}, {hubState}. Zone-based pricing applies for destinations outside the hub.</p>
          </div>
        </div>

        {/* Stories */}
        <StoriesBar />

        {/* Quick Actions */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Welcome back, {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-zinc-600">What do you need today?</p>
            </div>
            <Button 
              onClick={() => setShowNewJob(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
              data-testid="new-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Car, title: 'Ride', price: pricing.ride?.price, type: 'ride' },
              { icon: ShoppingBag, title: 'Food', price: pricing.food?.price, type: 'delivery', category: 'food' },
              { icon: ShoppingBag, title: 'Grocery', price: pricing.grocery?.price, type: 'delivery', category: 'grocery' },
              { icon: Package, title: 'Package', price: pricing.package?.price, type: 'delivery', category: 'package' },
            ].map((service) => (
              <button
                key={service.title}
                onClick={() => {
                  setNewJob({
                    ...newJob,
                    job_type: service.type,
                    delivery_category: service.category || '',
                  });
                  setShowNewJob(true);
                }}
                className="bg-white border border-zinc-200 rounded-xl p-4 text-left hover:border-orange-300 hover:shadow-md transition-all group"
                data-testid={`quick-${service.title.toLowerCase()}-btn`}
              >
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-orange-200 transition-colors">
                  <service.icon className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-zinc-900">{service.title}</h3>
                <p className="text-orange-500 font-bold">${service.price?.toFixed(2)}<span className="text-zinc-400 font-normal text-xs ml-1">+ zone</span></p>
              </button>
            ))}
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Button
              onClick={() => setShowBrowseRoutes(true)}
              variant="outline"
              className="border-orange-200 text-orange-600 hover:bg-orange-50"
              data-testid="browse-routes-btn"
            >
              <Route className="w-4 h-4 mr-2" />
              Routes
            </Button>
            <Button
              onClick={() => navigate('/tasks')}
              variant="outline"
              className="border-purple-200 text-purple-600 hover:bg-purple-50"
              data-testid="task-marketplace-btn"
            >
              <Tag className="w-4 h-4 mr-2" />
              Tasks
            </Button>
            <Button
              onClick={() => navigate('/people')}
              variant="outline"
              className="border-green-200 text-green-600 hover:bg-green-50"
              data-testid="people-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              People
            </Button>
            <Button
              onClick={() => navigate('/community')}
              variant="outline"
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
              data-testid="community-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Feed
            </Button>
          </div>

          {/* Map Toggle Button */}
          <div className="mt-4">
            <Button
              onClick={() => setShowMap(!showMap)}
              variant={showMap ? "default" : "outline"}
              className={showMap ? "w-full bg-orange-500 hover:bg-orange-600" : "w-full border-orange-200 text-orange-600 hover:bg-orange-50"}
              data-testid="toggle-map-btn"
            >
              <Map className="w-4 h-4 mr-2" />
              {showMap ? 'Hide Service Map' : 'View Service Map'}
            </Button>
          </div>

          {/* Interactive Map */}
          {showMap && (
            <div className="mt-4 fade-in">
              <InteractiveMap height="400px" showJobs={true} showRoutes={true} />
            </div>
          )}
        </div>

        {/* Active Jobs */}
        {activeJobs.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Active Jobs
            </h2>
            <div className="space-y-4">
              {activeJobs.map((job) => {
                const Icon = getJobIcon(job.job_type, job.delivery_category);
                const driverLocation = driverLocations[job.id];
                return (
                  <div
                    key={job.id}
                    className="bg-white border border-zinc-200 rounded-xl p-5 fade-in"
                    data-testid={`job-card-${job.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                          <Icon className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-zinc-900 capitalize">
                            {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                            {job.scheduled_at && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                Scheduled
                              </span>
                            )}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{job.pickup_address}</span>
                            <ArrowRight className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{job.dropoff_address}</span>
                          </div>
                          {job.scheduled_at && (
                            <div className="flex items-center gap-2 text-sm text-blue-600 mt-1">
                              <Clock className="w-4 h-4" />
                              <span>{new Date(job.scheduled_at).toLocaleString()}</span>
                            </div>
                          )}
                          {job.driver_name && (
                            <p className="text-sm text-zinc-600 mt-1">
                              Driver: <span className="font-medium">{job.driver_name}</span>
                            </p>
                          )}
                          
                          {/* Zone badge */}
                          {job.zone_name && job.zone_name !== 'Hub' && (
                            <span className="inline-flex items-center text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full mt-2">
                              {job.zone_name} (+${job.zone_addon?.toFixed(2)})
                            </span>
                          )}
                          
                          {/* Driver Location Indicator */}
                          {driverLocation && ['accepted', 'in_progress'].includes(job.status) && (
                            <button
                              onClick={() => setExpandedTracking(prev => ({ ...prev, [job.id]: !prev[job.id] }))}
                              className="flex items-center gap-2 text-sm text-green-600 mt-2 hover:text-green-700 transition-colors"
                            >
                              <Navigation className="w-4 h-4 animate-pulse" />
                              <span>Driver is {job.status === 'accepted' ? 'on the way to pickup' : 'en route to dropoff'}</span>
                              {expandedTracking[job.id] ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          
                          {/* Track Driver button when no live location yet */}
                          {!driverLocation && ['accepted', 'in_progress'].includes(job.status) && (
                            <button
                              onClick={() => setExpandedTracking(prev => ({ ...prev, [job.id]: !prev[job.id] }))}
                              className="flex items-center gap-2 text-sm text-orange-600 mt-2 hover:text-orange-700 transition-colors"
                            >
                              <Map className="w-4 h-4" />
                              <span>Track Driver</span>
                              {expandedTracking[job.id] ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-zinc-900">${job.price.toFixed(2)}</p>
                        <div className="flex gap-2 mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                            {job.status.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentColor(job.payment_status)}`}>
                            {job.payment_status}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Driver Tracking Map - Expandable */}
                    {expandedTracking[job.id] && ['accepted', 'in_progress'].includes(job.status) && (
                      <div className="mt-4 pt-4 border-t border-zinc-100">
                        <DriverTrackingMap 
                          job={job}
                          driverLocation={driverLocation}
                          height="280px"
                        />
                      </div>
                    )}
                    
                    {job.status === 'pending' && job.payment_status !== 'paid' && (
                      <div className="flex gap-3 mt-4 pt-4 border-t border-zinc-100">
                        <Button
                          onClick={() => handlePayment(job.id)}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          data-testid={`pay-job-${job.id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay Now
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleCancelJob(job.id)}
                          data-testid={`cancel-job-${job.id}`}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Past Jobs */}
        {pastJobs.length > 0 && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
              <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Past Jobs
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4 text-zinc-400" />
                  <Input
                    type="date"
                    value={dateFilter.start}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                    className="h-8 w-[130px] text-sm"
                    data-testid="date-filter-start"
                  />
                  <span className="text-zinc-400 text-sm">to</span>
                  <Input
                    type="date"
                    value={dateFilter.end}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                    className="h-8 w-[130px] text-sm"
                    data-testid="date-filter-end"
                  />
                </div>
                {(dateFilter.start || dateFilter.end) && (
                  <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setDateFilter({ start: '', end: '' })} data-testid="clear-date-filter">
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-3">
              {pastJobs
                .filter(job => {
                  if (!dateFilter.start && !dateFilter.end) return true;
                  const jobDate = job.created_at?.slice(0, 10);
                  if (dateFilter.start && jobDate < dateFilter.start) return false;
                  if (dateFilter.end && jobDate > dateFilter.end) return false;
                  return true;
                })
                .slice(0, 20).map((job) => {
                const Icon = getJobIcon(job.job_type, job.delivery_category);
                const canRate = job.status === 'completed' && job.driver_id && !ratedJobs.has(job.id);
                const hasRated = ratedJobs.has(job.id);
                return (
                  <div
                    key={job.id}
                    className="bg-white border border-zinc-200 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-zinc-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-zinc-900 capitalize">
                            {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                          </h3>
                          <p className="text-sm text-zinc-500">
                            {new Date(job.created_at).toLocaleDateString()}
                            {job.zone_name && job.zone_name !== 'Hub' && (
                              <span className="ml-2 text-blue-600">• {job.zone_name}</span>
                            )}
                            {job.driver_name && (
                              <span className="ml-2">• Driver: {job.driver_name}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                        <p className="font-semibold text-zinc-900">${job.price.toFixed(2)}</p>
                      </div>
                    </div>
                    
                    {/* Rating & Tip Section */}
                    {job.status === 'completed' && job.driver_id && (
                      <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-3 flex-wrap">
                        {canRate ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenRating(job)}
                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                            data-testid={`rate-job-${job.id}`}
                          >
                            <Star className="w-4 h-4 mr-2" />
                            Rate this {job.job_type === 'ride' ? 'ride' : 'delivery'}
                          </Button>
                        ) : hasRated ? (
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span>Rated</span>
                          </div>
                        ) : null}
                        
                        {tippedJobs[job.id] ? (
                          <div className="flex items-center gap-1.5 text-sm text-pink-600" data-testid={`tipped-badge-${job.id}`}>
                            <Heart className="w-4 h-4 fill-pink-500" />
                            <span>Tipped ${tippedJobs[job.id].toFixed(2)}</span>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setJobToTip(job); setShowTipDialog(true); }}
                            className="text-pink-600 border-pink-200 hover:bg-pink-50"
                            data-testid={`tip-job-${job.id}`}
                          >
                            <Heart className="w-4 h-4 mr-1" />
                            Tip
                          </Button>
                        )}

                        {(() => {
                          const completedAt = new Date(job.updated_at || job.created_at);
                          const daysSince = (Date.now() - completedAt.getTime()) / (1000 * 60 * 60 * 24);
                          if (daysSince > 7) return null;
                          return (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setJobToDispute(job); setShowDisputeDialog(true); }}
                              className="text-zinc-500 hover:text-red-600"
                              data-testid={`dispute-job-${job.id}`}
                            >
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              Report Issue
                            </Button>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {jobs.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No jobs yet</h3>
            <p className="text-zinc-600 mb-4">Request your first ride or delivery!</p>
            <Button 
              onClick={() => setShowNewJob(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        )}
      </main>

      {/* New Job Dialog */}
      <Dialog open={showNewJob} onOpenChange={setShowNewJob}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>New Request</DialogTitle>
            <DialogDescription className="text-xs">
              Service operates through {hubName}, {hubState} hub
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateJob} className="space-y-3">
            <JobAutofillInput
              onFill={(fields) => {
                setNewJob((prev) => ({
                  ...prev,
                  job_type: fields.job_type || prev.job_type,
                  delivery_category: fields.delivery_category || prev.delivery_category || '',
                  pickup_address: fields.pickup_address || prev.pickup_address,
                  dropoff_address: fields.dropoff_address || prev.dropoff_address,
                  notes: fields.notes || prev.notes,
                  passenger_count: fields.passenger_count?.toString() || prev.passenger_count,
                  recipient_name: fields.recipient_name || prev.recipient_name,
                  recipient_phone: fields.recipient_phone || prev.recipient_phone,
                  order_details: fields.order_details || prev.order_details,
                  delivery_instructions: fields.delivery_instructions || prev.delivery_instructions,
                  recipient_dob: fields.recipient_dob || prev.recipient_dob,
                  prescription_number: fields.prescription_number || prev.prescription_number,
                }));
              }}
            />
            {/* Service Type & Category - Compact Row on larger screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">Service Type</Label>
                <Select
                  value={newJob.job_type}
                  onValueChange={(value) => setNewJob({ ...newJob, job_type: value, delivery_category: '' })}
                >
                  <SelectTrigger data-testid="job-type-select" className="h-9">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ride">Ride - ${pricing.ride?.price?.toFixed(2)}</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newJob.job_type === 'delivery' && (
                <div className="space-y-1">
                  <Label className="text-sm">Category</Label>
                  <Select
                    value={newJob.delivery_category}
                    onValueChange={(value) => setNewJob({ ...newJob, delivery_category: value })}
                  >
                    <SelectTrigger data-testid="delivery-category-select" className="h-9">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="food">Food - ${pricing.food?.price?.toFixed(2)}</SelectItem>
                      <SelectItem value="grocery">Grocery - ${pricing.grocery?.price?.toFixed(2)}</SelectItem>
                      <SelectItem value="pharmacy">Pharmacy - ${pricing.pharmacy?.price?.toFixed(2)}</SelectItem>
                      <SelectItem value="package">Package - ${pricing.package?.price?.toFixed(2)}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Addresses - Compact */}
            <div className="space-y-1">
              <Label className="text-sm">Pickup Address</Label>
              <Input
                placeholder={`e.g., 123 Main St, ${hubName}, IL`}
                value={newJob.pickup_address}
                onChange={(e) => setNewJob({ ...newJob, pickup_address: e.target.value })}
                required
                data-testid="pickup-address-input"
                className="h-9 text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Dropoff Address</Label>
              <Input
                placeholder="e.g., 456 Oak Ave, O'Fallon, IL"
                value={newJob.dropoff_address}
                onChange={(e) => setNewJob({ ...newJob, dropoff_address: e.target.value })}
                required
                data-testid="dropoff-address-input"
                className="h-9 text-sm"
              />
            </div>

            {/* Ride-specific: passenger count */}
            {newJob.job_type === 'ride' && (
              <div className="space-y-1">
                <Label className="text-sm">Passengers (optional)</Label>
                <Input
                  type="number"
                  min={1}
                  max={8}
                  placeholder="1"
                  value={newJob.passenger_count}
                  onChange={(e) => setNewJob({ ...newJob, passenger_count: e.target.value })}
                  data-testid="passenger-count-input"
                  className="h-9 text-sm"
                />
              </div>
            )}

            {/* Delivery-specific: recipient + order details */}
            {newJob.job_type === 'delivery' && (
              <div className="space-y-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                  Delivery Details
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Recipient Name *</Label>
                    <Input
                      placeholder="Full name"
                      value={newJob.recipient_name}
                      onChange={(e) => setNewJob({ ...newJob, recipient_name: e.target.value })}
                      required
                      data-testid="recipient-name-input"
                      className="h-9 text-sm bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Recipient Phone *</Label>
                    <Input
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={newJob.recipient_phone}
                      onChange={(e) => setNewJob({ ...newJob, recipient_phone: e.target.value })}
                      required
                      data-testid="recipient-phone-input"
                      className="h-9 text-sm bg-white"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Order / Item Details *</Label>
                  <Textarea
                    placeholder={newJob.delivery_category === 'food'
                      ? 'e.g., 2× large pepperoni pizzas, 1× Caesar salad'
                      : newJob.delivery_category === 'grocery'
                      ? 'e.g., Milk, bread, eggs, 2× rotisserie chickens — list at the store'
                      : newJob.delivery_category === 'pharmacy'
                      ? 'e.g., Rx pickup for Jane Doe — 2 prescriptions'
                      : 'Describe what is being delivered'}
                    value={newJob.order_details}
                    onChange={(e) => setNewJob({ ...newJob, order_details: e.target.value })}
                    required
                    data-testid="order-details-input"
                    className="min-h-[60px] text-sm bg-white resize-none"
                    rows={2}
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Delivery Instructions (optional)</Label>
                    {newJob.delivery_instructions && newJob.delivery_instructions.trim().length > 6 && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const polished = await aiDeliveryInstructions(
                              newJob.delivery_instructions,
                              newJob.dropoff_address,
                            );
                            if (polished) setNewJob({ ...newJob, delivery_instructions: polished });
                          } catch (e) {
                            toast.error('AI polish failed');
                          }
                        }}
                        className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
                        data-testid="ai-polish-instructions-btn"
                      >
                        <SparklesIcon className="w-3 h-3" /> Polish with AI
                      </button>
                    )}
                  </div>
                  <Input
                    placeholder="Gate code, apartment number, leave at door, etc."
                    value={newJob.delivery_instructions}
                    onChange={(e) => setNewJob({ ...newJob, delivery_instructions: e.target.value })}
                    data-testid="delivery-instructions-input"
                    className="h-9 text-sm bg-white"
                  />
                </div>

                {/* Pharmacy-specific: DOB + Rx number */}
                {newJob.delivery_category === 'pharmacy' && (
                  <div className="space-y-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                      Pharmacy — Identity Verification
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Recipient DOB *</Label>
                        <Input
                          type="date"
                          value={newJob.recipient_dob}
                          onChange={(e) => setNewJob({ ...newJob, recipient_dob: e.target.value })}
                          max={new Date().toISOString().slice(0, 10)}
                          required
                          data-testid="recipient-dob-input"
                          className="h-9 text-sm bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Rx # (optional)</Label>
                        <Input
                          placeholder="Prescription number"
                          value={newJob.prescription_number}
                          onChange={(e) => setNewJob({ ...newJob, prescription_number: e.target.value })}
                          data-testid="prescription-number-input"
                          className="h-9 text-sm bg-white"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-blue-700">
                      Driver will verify the recipient's DOB on delivery before handing over medication.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Schedule Toggle - Inline */}
            <div className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                id="schedule-toggle"
                checked={newJob.is_scheduled}
                onChange={(e) => setNewJob({ ...newJob, is_scheduled: e.target.checked })}
                className="w-4 h-4 text-orange-500 rounded border-zinc-300 focus:ring-orange-500"
                data-testid="schedule-toggle"
              />
              <Label htmlFor="schedule-toggle" className="cursor-pointer flex items-center gap-1.5 text-sm">
                <Clock className="w-3.5 h-3.5 text-zinc-500" />
                Schedule for later
              </Label>
              {newJob.is_scheduled && (
                <Input
                  type="datetime-local"
                  value={newJob.scheduled_at}
                  onChange={(e) => setNewJob({ ...newJob, scheduled_at: e.target.value })}
                  min={new Date().toISOString().slice(0, 16)}
                  required={newJob.is_scheduled}
                  data-testid="scheduled-datetime-input"
                  className="h-8 text-sm flex-1 ml-2"
                />
              )}
            </div>

            {/* Notes - Smaller */}
            <div className="space-y-1">
              <Label className="text-sm">Notes (optional)</Label>
              <Textarea
                placeholder="Any special instructions..."
                value={newJob.notes}
                onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
                data-testid="job-notes-input"
                className="min-h-[60px] text-sm resize-none"
                rows={2}
              />
            </div>

            {/* Matched Driver Routes - Compact Collapsible */}
            {matchedRoutes.length > 0 && !loadingMatches && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <button
                  type="button"
                  onClick={() => setShowBrowseRoutes(true)}
                  className="w-full flex items-center justify-between text-blue-700 hover:text-blue-800"
                >
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4" />
                    <span className="font-medium text-sm">Suggested Routes ({matchedRoutes.length})</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-green-600 font-medium">from ${matchedRoutes[0]?.price_per_seat?.toFixed(2)}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              </div>
            )}

            {/* Route Validation Result - Compact */}
            {validatingRoute && (
              <div className="flex items-center gap-2 text-zinc-500 py-1">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Validating route...</span>
              </div>
            )}

            {routeValidation && !validatingRoute && (
              <>
                {!routeValidation.is_valid ? (
                  <Alert className="border-red-200 bg-red-50 py-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 text-sm">
                      {routeValidation.error_message}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-medium text-sm">Route valid</span>
                        {routeValidation.zone_name !== 'Hub' && (
                          <span className="text-xs bg-green-100 px-1.5 py-0.5 rounded-full">
                            {routeValidation.zone_name}
                          </span>
                        )}
                      </div>
                      <span className="text-lg font-bold text-orange-500">${routeValidation.total_price.toFixed(2)}</span>
                    </div>
                    <div className="flex gap-4 text-xs text-zinc-500 mt-1">
                      <span>Base: ${routeValidation.base_price.toFixed(2)}</span>
                      {routeValidation.zone_addon > 0 && (
                        <span>Zone: +${routeValidation.zone_addon.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-1">
              <Button type="button" variant="outline" onClick={() => {
                setShowNewJob(false);
                setRouteValidation(null);
                setMatchedRoutes([]);
              }} className="flex-1 h-10">
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 h-10 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={
                  submitting || 
                  !newJob.job_type || 
                  (newJob.job_type === 'delivery' && !newJob.delivery_category) ||
                  (newJob.job_type === 'delivery' && (!newJob.recipient_name.trim() || !newJob.recipient_phone.trim() || !newJob.order_details.trim())) ||
                  (newJob.job_type === 'delivery' && newJob.delivery_category === 'pharmacy' && !newJob.recipient_dob) ||
                  validatingRoute ||
                  (routeValidation && !routeValidation.is_valid) ||
                  (newJob.is_scheduled && !newJob.scheduled_at)
                }
                data-testid="submit-job-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {newJob.is_scheduled ? 'Scheduling...' : 'Creating...'}
                  </>
                ) : (
                  newJob.is_scheduled ? 'Schedule Request' : 'Create Request'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <RatingDialog
        open={showRatingDialog}
        onOpenChange={setShowRatingDialog}
        job={jobToRate}
        onRatingSubmitted={handleRatingSubmitted}
      />

      {/* Tip Dialog */}
      {jobToTip && (
        <TipDialog
          open={showTipDialog}
          onOpenChange={setShowTipDialog}
          job={jobToTip}
        />
      )}

      {/* Dispute Dialog */}
      <DisputeDialog
        open={showDisputeDialog}
        onOpenChange={setShowDisputeDialog}
        job={jobToDispute}
        userRole="user"
      />

      {/* Browse Driver Routes Dialog */}
      <Dialog open={showBrowseRoutes} onOpenChange={setShowBrowseRoutes}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Driver Routes</DialogTitle>
            <DialogDescription>
              Book a seat on a driver's scheduled route
            </DialogDescription>
          </DialogHeader>
          <BrowseRoutes onClose={() => setShowBrowseRoutes(false)} />
        </DialogContent>
      </Dialog>

      <AIConciergeLauncher />
    </div>
  );
};

export default UserDashboard;
